import { useCallback, useMemo, useState } from 'react';
import type { AgentItem, AgentSession } from '../../domain/types';
import { mapAgentSessionToAgentItem } from '../../domain/mappers';
import { agentsClient } from '../../services/clients';

export interface AgentsSliceOptions {
  beginWrite: () => void;
  retry: (source?: string) => Promise<void>;
}

export function useAgentsSlice({ beginWrite, retry }: AgentsSliceOptions) {
  const [agents, setAgents] = useState<AgentItem[]>([]);
  const [rawAgentSessions, setRawAgentSessions] = useState<AgentSession[]>([]);

  const toggleAgentRunning = useCallback(async (agentName: string) => {
    beginWrite();
    setAgents((prev) =>
      prev.map((agent) => {
        if (agent.name === agentName) {
          const running = !agent.running;
          return {
            ...agent,
            running,
            state: running ? 'corriendo' : 'en pausa',
            secondAction: running ? 'Detener' : 'Reanudar',
          };
        }
        return agent;
      })
    );

    try {
      const res = await agentsClient.toggleAgentRunning(agentName);
      const confirmed = mapAgentSessionToAgentItem(res.data);
      setAgents((prev) => {
        const idx = prev.findIndex((a) => a.name === agentName);
        if (idx === -1) return [...prev, confirmed];
        const next = [...prev];
        next[idx] = confirmed;
        return next;
      });
    } catch {
      await retry('agents');
    }
  }, [beginWrite, retry]);

  const launchAgentTask = useCallback(async (agentName: string, taskPrompt: string) => {
    const trimmed = taskPrompt.trim();
    if (!trimmed) return;
    beginWrite();

    const timeStr = new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const addedTokens = 14500;

    setAgents((prev) =>
      prev.map((agent) => {
        if (agent.name === agentName) {
          const currentTokensNum = agent.tokensConsumed || (agent.tokens.includes('k') ? parseInt(agent.tokens, 10) * 1000 : 120000);
          const newTokensNum = currentTokensNum + addedTokens;
          const currentCostNum = agent.costUsd || parseFloat(agent.cost.replace('$', '')) || 2.0;
          const newCostNum = currentCostNum + 0.18;
          const existingLogs = agent.logs || [];

          return {
            ...agent,
            running: true,
            state: 'corriendo',
            secondAction: 'Detener',
            last: `Ejecutando: ${trimmed}`,
            tokensConsumed: newTokensNum,
            tokensFormatted: `${Math.round(newTokensNum / 1000)}k tok`,
            tokens: `${Math.round(newTokensNum / 1000)}k tok`,
            costUsd: newCostNum,
            costFormatted: `$${newCostNum.toFixed(2)}`,
            cost: `$${newCostNum.toFixed(2)}`,
            logs: [
              ...existingLogs,
              {
                id: `log-${Date.now()}-1`,
                timestamp: timeStr,
                level: 'cmd' as const,
                message: `> Tarea: "${trimmed}"`,
              },
              {
                id: `log-${Date.now()}-2`,
                timestamp: timeStr,
                level: 'info' as const,
                message: `Analizando AST y dependencias en rama ${agent.branch}...`,
              },
              {
                id: `log-${Date.now()}-3`,
                timestamp: timeStr,
                level: 'success' as const,
                message: `+${addedTokens.toLocaleString()} tokens consumidos. Ejecución en curso.`,
              },
            ],
          };
        }
        return agent;
      })
    );

    try {
      const res = await agentsClient.launchAgentTask(agentName, trimmed);
      const confirmed = mapAgentSessionToAgentItem(res.data);
      setAgents((prev) => {
        const idx = prev.findIndex((a) => a.name === agentName);
        if (idx === -1) return [...prev, confirmed];
        const next = [...prev];
        const current = prev[idx];
        next[idx] = { ...current, ...confirmed, logs: confirmed.logs || current.logs };
        return next;
      });
    } catch {
      await retry('agents');
    }
  }, [beginWrite, retry]);

  return useMemo(
    () => ({
      agents,
      setAgents,
      rawAgentSessions,
      setRawAgentSessions,
      toggleAgentRunning,
      launchAgentTask,
    }),
    [agents, rawAgentSessions, toggleAgentRunning, launchAgentTask],
  );
}
